import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type ProductStatus = 'draft' | 'public' | 'sold_out' | 'upcoming' | 'archived';
export type ProductType = 'simple' | 'bundle';

export interface ProducerProduct {
  id: string;
  producer_id: string;
  title: string;
  description: string | null;
  price_ore: number;
  currency: string;
  status: ProductStatus;
  product_type: ProductType;
  seo_title: string | null;
  seo_description: string | null;
  seo_image_url: string | null;
  stock_limit: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /** Bundle items (only populated for bundle products, from edge function) */
  bundle_items?: Array<{
    product_id: string;
    quantity: number;
    sort_order: number;
  }>;
}

export interface CreateProductData {
  title: string;
  description?: string;
  price_ore: number;
  currency?: string;
  status?: ProductStatus;
  product_type?: 'simple' | 'bundle';
  bundle_items?: Array<{ product_id: string; quantity: number; sort_order: number }>;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_image_url?: string | null;
  pickup_instructions?: string | null;
  stock_limit?: number | null;
}

export interface UpdateProductData extends Partial<CreateProductData> {
  id: string;
}

export interface UseProducerProductsResult {
  products: ProducerProduct[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createProduct: (data: CreateProductData) => Promise<ProducerProduct | null>;
  updateProduct: (data: UpdateProductData) => Promise<ProducerProduct | null>;
  deleteProduct: (id: string) => Promise<boolean>;
  softDeleteProduct: (id: string) => Promise<boolean>;
  reorderProducts: (orderedIds: string[]) => Promise<boolean>;
  deleting: string | null;
}

/**
 * Hook for managing producer products
 */
export function useProducerProducts(producerId: string | undefined): UseProducerProductsResult {
  const [products, setProducts] = useState<ProducerProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    if (!producerId) {
      setProducts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session?.access_token) {
        setError('Inte inloggad');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/producer-products?producer_id=${encodeURIComponent(producerId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        const detail = result.error || result.message || `HTTP ${response.status}`;
        console.error('[useProducerProducts] fetch failed:', { status: response.status, detail, producerId });
        throw new Error(detail);
      }

      setProducts(result.products || []);
    } catch (err: any) {
      console.error('[useProducerProducts] Error fetching products:', err);
      setError('Kunde inte hämta produkter');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [producerId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const createProduct = async (data: CreateProductData): Promise<ProducerProduct | null> => {
    if (!producerId) return null;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/producer-products`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            producer_id: producerId,
            ...data,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        // Check for limit_reached error from DB trigger
        const errorMsg = result.error || result.details || '';
        if (errorMsg.includes('limit_reached') || result.code === 'P0001') {
          const match = errorMsg.match(/max[:\s]*(\d+)/i);
          const limit = match ? match[1] : '?';
          const error = new Error(`Du har nått gränsen för din plan (max ${limit} produkter). Uppgradera för att skapa fler.`);
          (error as any).isLimitError = true;
          throw error;
        }
        throw new Error(result.error || 'Failed to create product');
      }

      await fetchProducts();
      return result.product;
    } catch (err) {
      console.error('Error creating product:', err);
      throw err;
    }
  };

  const updateProduct = async (data: UpdateProductData): Promise<ProducerProduct | null> => {
    try {
      // PATCH with id in query param for proper CORS handling
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/producer-products?id=${data.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify(data),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update product');
      }

      await fetchProducts();
      return result.product;
    } catch (err) {
      console.error('Error updating product:', err);
      throw err;
    }
  };

  const deleteProduct = async (id: string): Promise<boolean> => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/producer-products?id=${id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete product');
      }

      await fetchProducts();
      return true;
    } catch (err) {
      console.error('Error deleting product:', err);
      throw err;
    }
  };

  /**
   * Soft delete via edge function (archives in DB).
   */
  const softDeleteProduct = async (id: string): Promise<boolean> => {
    setDeleting(id);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('delete-producer-product', {
        body: { productId: id },
      });

      if (invokeError) {
        throw new Error(invokeError.message || 'Delete failed');
      }

      if (data?.error) {
        throw new Error(data.details || data.error);
      }

      await fetchProducts();
      return true;
    } catch (err) {
      console.error('Error deleting product:', err);
      throw err;
    } finally {
      setDeleting(null);
    }
  };

  /**
   * Reorder products — optimistic UI, persists via edge function PUT.
   */
  const reorderProducts = async (orderedIds: string[]): Promise<boolean> => {
    if (!producerId) return false;

    // Optimistic: reorder local state immediately
    const prev = [...products];
    const idMap = new Map(products.map(p => [p.id, p]));
    const reordered = orderedIds.map(id => idMap.get(id)).filter(Boolean) as ProducerProduct[];
    // Append any products not in orderedIds (shouldn't happen, safety net)
    const remaining = products.filter(p => !orderedIds.includes(p.id));
    setProducts([...reordered, ...remaining]);

    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/producer-products`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || ''}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            action: 'reorder',
            producer_id: producerId,
            ordered_ids: orderedIds,
          }),
        }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Reorder failed');
      }

      return true;
    } catch (err) {
      console.error('Error reordering products:', err);
      // Revert on failure
      setProducts(prev);
      throw err;
    }
  };

  return {
    products,
    loading,
    error,
    refetch: fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    softDeleteProduct,
    reorderProducts,
    deleting,
  };
}
